import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useSession, useAttendance, useMarkAttendance, useEnrolledStudents, useMaterials } from '../../lib/hooks';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { 
  ArrowLeft, 
  Loader2, 
  Calendar,
  Clock,
  Save,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  ExternalLink,
  File,
  Play
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const materialTypeIcons = {
  pdf: File,
  video: Play,
  exercise: FileText,
  link: ExternalLink,
  other: FileText,
};

const SessionView = () => {
  const { id: courseId, sessionId } = useParams();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [attendanceData, setAttendanceData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // RBAC
  const isAdmin = user?.role === 'admin';
  const isRespPeda = user?.role === 'resp_peda';
  const isTeacher = user?.role === 'teacher';
  const canMarkAttendance = isAdmin || isRespPeda || isTeacher;

  // Queries
  const { data: session, isLoading } = useSession(sessionId);
  const { data: existingAttendance } = useAttendance(sessionId);
  const { data: enrolledStudents } = useEnrolledStudents(courseId);
  const { data: materials } = useMaterials(courseId, sessionId);

  // Mutation
  const markAttendance = useMarkAttendance(sessionId, courseId);

  // Initialize attendance data from existing records
  useEffect(() => {
    if (existingAttendance && enrolledStudents) {
      const initial = {};
      enrolledStudents.forEach(student => {
        const existing = existingAttendance.find(a => a.student_id === student.id);
        initial[student.id] = existing?.status || null;
      });
      setAttendanceData(initial);
    }
  }, [existingAttendance, enrolledStudents]);

  const handleStatusChange = (studentId, status) => {
    setAttendanceData(prev => ({
      ...prev,
      [studentId]: prev[studentId] === status ? null : status
    }));
    setHasChanges(true);
  };

  const handleSaveAttendance = async () => {
    const attendances = Object.entries(attendanceData)
      .filter(([_, status]) => status !== null)
      .map(([student_id, status]) => ({ student_id, status }));

    if (attendances.length === 0) {
      toast.error('Aucune présence à enregistrer');
      return;
    }

    try {
      await markAttendance.mutateAsync(attendances);
      toast.success('Présences enregistrées');
      setHasChanges(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    }
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold mb-2">Session non trouvée</h2>
        <Button variant="outline" onClick={() => navigate(`/courses/${courseId}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au cours
        </Button>
      </div>
    );
  }

  const isPast = new Date(session.date) < new Date();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="session-view-page">
      {/* Back button */}
      <Button variant="ghost" onClick={() => navigate(`/courses/${courseId}`)} className="mb-2">
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour au cours
      </Button>

      {/* Session Header */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="font-heading text-2xl">{session.title}</CardTitle>
              <CardDescription className="flex items-center gap-4 mt-2">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDateTime(session.date)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {session.duration_minutes} minutes
                </span>
              </CardDescription>
            </div>
            {isPast ? (
              <Badge variant="secondary">Passée</Badge>
            ) : (
              <Badge className="bg-success/10 text-success">À venir</Badge>
            )}
          </div>
        </CardHeader>
      </Card>

      {/* Attendance Section */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-success" />
              Feuille de présence
            </CardTitle>
            {canMarkAttendance && hasChanges && (
              <Button onClick={handleSaveAttendance} disabled={markAttendance.isPending}>
                {markAttendance.isPending ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Enregistrer
              </Button>
            )}
          </div>
          <CardDescription>
            Cliquez sur un statut pour marquer la présence de chaque étudiant
          </CardDescription>
        </CardHeader>
        <CardContent>
          {enrolledStudents?.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              Aucun étudiant inscrit à ce cours
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('students_title', lang) === 'Studierende' ? 'Studierende/r' : 'Étudiant'}</TableHead>
                  <TableHead className="text-center">{lang === 'de' ? 'Anwesend' : 'Présent'}</TableHead>
                  <TableHead className="text-center">Retard</TableHead>
                  <TableHead className="text-center">{lang === 'de' ? 'Abwesend' : 'Absent'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {enrolledStudents?.map((student) => {
                  const status = attendanceData[student.id];
                  return (
                    <TableRow key={student.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9">
                            <AvatarFallback className="bg-primary text-white">
                              {student.first_name[0]}{student.last_name[0]}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{student.first_name} {student.last_name}</p>
                            <p className="text-xs text-muted-foreground">{student.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant={status === 'present' ? 'default' : 'outline'}
                          size="sm"
                          className={status === 'present' ? 'bg-success hover:bg-success/90' : ''}
                          onClick={() => canMarkAttendance && handleStatusChange(student.id, 'present')}
                          disabled={!canMarkAttendance}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant={status === 'late' ? 'default' : 'outline'}
                          size="sm"
                          className={status === 'late' ? 'bg-warning hover:bg-warning/90' : ''}
                          onClick={() => canMarkAttendance && handleStatusChange(student.id, 'late')}
                          disabled={!canMarkAttendance}
                        >
                          <AlertCircle className="w-4 h-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant={status === 'absent' ? 'default' : 'outline'}
                          size="sm"
                          className={status === 'absent' ? 'bg-destructive hover:bg-destructive/90' : ''}
                          onClick={() => canMarkAttendance && handleStatusChange(student.id, 'absent')}
                          disabled={!canMarkAttendance}
                        >
                          <XCircle className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Materials Section */}
      {materials && materials.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-accent" />
              Matériaux de cette session ({materials.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {materials.map((material) => {
                const Icon = materialTypeIcons[material.type] || FileText;
                return (
                  <div key={material.id} className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                        <Icon className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">{material.title}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(material.created_at)}
                        </p>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <a href={material.url} target="_blank" rel="noopener noreferrer">
                        <ExternalLink className="w-4 h-4 mr-1" />
                        Ouvrir
                      </a>
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SessionView;
